import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Zap, 
  Phone, 
  Clock, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Headphones,
  Timer,
  X,
  Sparkles,
  Lightbulb,
  PhoneCall,
  Eye,
  MessageSquare,
  TrendingUp,
  Heart,
  FastForward
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function SaibaMaisReportarPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-orange-950/20 dark:via-background dark:to-red-950/20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-orange-600" />
              <span className="font-medium">Suporte Inteligente</span>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-6 shadow-lg">
            <PhoneCall className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent mb-4">
            Por que reportar problemas diretamente no app?
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Seu feedback faz toda a diferença na construção de um UrbNet melhor para toda a comunidade
          </p>
        </div>

        {/* Vantagens do Reporte Direto */}
        <div className="grid gap-6 mb-12">
          <Card className="group hover:shadow-lg transition-all duration-300 border-primary/20">
            <CardContent className="p-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    ⚡ Mais Rápido e Direto
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Seu relato chega diretamente aos nossos desenvolvedores em minutos, sem passar por filtros ou intermediários. 
                    Cada problema é analisado individualmente pela equipe técnica.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-lg transition-all duration-300 border-secondary/20">
            <CardContent className="p-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-secondary to-secondary/80 rounded-xl flex items-center justify-center">
                  <Eye className="h-6 w-6 text-secondary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    👀 Não se Perde no Meio
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Diferente de outros canais de suporte, onde seu feedback pode se afogar em meio a milhares de solicitações, 
                    aqui cada relato é importante e recebe atenção especial da nossa equipe.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-lg transition-all duration-300 border-accent/20">
            <CardContent className="p-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-accent to-accent/80 rounded-xl flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-accent-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    💬 Podemos Conversar
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Se precisarmos de mais detalhes sobre o problema, podemos te contatar diretamente para entender melhor 
                    a situação e resolver o problema mais rapidamente.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Impacto na Comunidade */}
        <Card className="mb-12 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/1">
          <CardContent className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-7 w-7 text-primary-foreground" />
              </div>
              <h3 className="text-2xl font-bold">📈 Impacto Real na Comunidade</h3>
            </div>
            
            <div className="grid gap-4">
              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-lg">
                <Users className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="font-medium">Cada report aqui ajuda a melhorar o app para milhares de usuários</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-lg">
                <FastForward className="h-5 w-5 text-secondary flex-shrink-0" />
                <span className="font-medium">Problemas corrigidos rapidamente beneficiam toda a comunidade</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-lg">
                <Heart className="h-5 w-5 text-accent flex-shrink-0" />
                <span className="font-medium">Você está contribuindo para um UrbNet melhor para todos</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rodapé */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Heart className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="font-medium text-foreground">
                Seu feedback direto no app faz toda a diferença! Juntos construímos um UrbNet melhor para toda a comunidade.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button 
            onClick={() => navigate(-1)}
            size="lg"
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Agora entendi melhor!
          </Button>
        </div>
      </div>
    </div>
  );
}
